import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Undo2 } from 'lucide-react';
import { Button } from '../../Button';
import { Modal } from '../../common/Modal';
import { useApplicationReview } from '../../../contexts/ApplicationReviewContext';
import { correctionReasons } from '../../../data/applicationReview';
import { can } from '../../../utils/applicationQueue';
import type { OwnerApplication } from '../../../types/owner';
interface Props {
  app: OwnerApplication | null;
  onClose: () => void;
  initialReasons?: string[];
}
export function ReturnForCorrectionModal({
  app,
  onClose,
  initialReasons = []
}: Props) {
  const {
    returnForCorrection,
    role
  } = useApplicationReview();
  const [reasons, setReasons] = useState<string[]>(initialReasons);
  const [detail, setDetail] = useState('');
  const [instruction, setInstruction] = useState('');
  const [comment, setComment] = useState('');
  useEffect(() => {
    if (app) {
      setReasons(initialReasons);
      setDetail('');
      setInstruction('');
      setComment('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.id]);
  const toggle = (r: string) => setReasons(rs => rs.includes(r) ? rs.filter(x => x !== r) : [...rs, r]);
  const allowed = can(role, 'return');
  const valid = reasons.length > 0 && detail.trim().length > 4 && instruction.trim().length > 4;
  const submit = () => {
    if (!app || !valid) return;
    returnForCorrection(app, {
      reasons,
      detail,
      instruction,
      comment
    });
    toast.success(`${app.id} সংশোধনের জন্য ফেরত পাঠানো হয়েছে — মালিককে নোটিফিকেশন পাঠানো হয়েছে`);
    onClose();
  };
  return <Modal open={Boolean(app)} onClose={onClose} title="সংশোধনের জন্য আবেদন ফেরত" width="max-w-2xl" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-511" data-mp="2dh7baosana4">বাতিল</Button>
          <Button onClick={submit} disabled={!valid || !allowed} className="bg-amber-600 hover:bg-amber-700" data-id="element-512" data-mp="3ui1n5r8v03dd"><Undo2 className="h-4 w-4" data-id="element-513" data-mp="2r74abnnedtfi" />সংশোধনের জন্য ফেরত পাঠান</Button>
        </>} data-id="element-510" data-mp="2n2dcoez595gz">
      {app && <div className="space-y-4" data-id="element-514" data-mp="2w5yv2v4xds29">
          <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600" data-id="element-515" data-mp="2w4xds8xbg9c3">
            <b className="text-navy-900" data-id="element-516" data-mp="316d1y9usivyp">{app.id}</b> • {app.vehicleReg} • {app.ownerName}
          </p>
          {!allowed && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-id="element-517" data-mp="355lrf2iss7og">আপনার বর্তমান ভূমিকায় আবেদন ফেরত পাঠানোর অনুমতি নেই।</p>}
          <fieldset data-id="element-518" data-mp="28ete2cn90tqd">
            <legend className="mb-2 text-sm font-semibold text-navy-900" data-id="element-519" data-mp="2dfhc3i9waau3">সংশোধনের কারণ নির্বাচন করুন <span className="text-red-600" data-id="element-520" data-mp="2hhcekq3kau9g">*</span></legend>
            <div className="grid gap-2 sm:grid-cols-2" data-id="element-521" data-mp="2hfrll4wwbjdp">
              {correctionReasons.map(r => <label key={r} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${reasons.includes(r) ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-slate-200 text-slate-700'}`} data-id="element-522" data-mp="2m9c7hyl7smwj">
                  <input type="checkbox" checked={reasons.includes(r)} onChange={() => toggle(r)} className="h-4 w-4 accent-amber-600" data-id="element-523" data-mp="1m88r6bz030yk" />
                  {r}
                </label>)}
            </div>
          </fieldset>
          <TextArea id="rc-detail" label="সংশোধনের বিবরণ" required value={detail} onChange={setDetail} placeholder="কোন তথ্যে সমস্যা পাওয়া গেছে তা লিখুন" data-id="element-524" data-mp="1d6rcmy18r7bz" />
          <TextArea id="rc-instr" label="সংশোধনের নির্দেশনা (মালিক অ্যাপে দেখানো হবে)" required value={instruction} onChange={setInstruction} placeholder="মালিককে কী করতে হবে তা সহজ ভাষায় লিখুন" data-id="element-525" data-mp="18asi0spbkt34" />
          <TextArea id="rc-comment" label="মন্তব্য (অভ্যন্তরীণ)" value={comment} onChange={setComment} rows={2} data-id="element-526" data-mp="ygtky843rzi1" />
          <p className="text-xs text-slate-500" data-id="element-527" data-mp="pi86op5nz6n2">ফেরত পাঠানোর সাথে সাথে মালিকের অ্যাপে অবস্থা “সংশোধন প্রয়োজন” হবে এবং “তথ্য সংশোধন করুন” বাটন সক্রিয় হবে। পূর্বের জমা ও মন্তব্য সংরক্ষিত থাকবে।</p>
        </div>}
    </Modal>;
}
function TextArea({
  id,
  label,
  value,
  onChange,
  required,
  placeholder,
  rows = 3
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  rows?: number;
}) {
  return <div data-id="element-528" data-mp="2kkbomvif3kne">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-slate-600" data-id="element-529" data-mp="2kmxsak26tg0c">{label}{required && <span className="text-red-600" data-id="element-530" data-mp="25u49oprmcjrg"> *</span>}</label>
      <textarea id={id} rows={rows} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100" data-id="element-531" data-mp="2au9y7x2ldq5b" />
    </div>;
}